import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  cryovialFields,
  hasOverride,
  resetCryovial,
  resolveCryovial,
  updateCryovial,
  validateCryovials,
  type CryovialDraft,
  type CryovialField,
  type CryovialIssue,
  type CryovialValues,
} from '../../lib/cryovialDraft';

type PositionOption = { id: string; label: string; occupied: boolean };
type Props = {
  positions: PositionOption[];
  initialPositionIds: string[];
  path: string;
  cellLines: { value: string; label: string; code: string }[];
  materials: { id: string; cellLineId: string; label: string; type: string }[];
  submitting: boolean;
  error: string;
  result: ReactNode;
  canExecute: boolean;
  onClose: () => void;
  onSubmit: (
    rows: ReturnType<typeof resolveCryovial>[],
    reason: string,
  ) => Promise<void>;
  onCreateCellLine: () => void;
  onCreateMaterial: (cellLineId: string) => void;
};

export function CryovialEntryModal(props: Props) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [modal, holder] = Modal.useModal();
  const [common, setCommon] = useState<CryovialValues>({
    freezeDate: dayjs().format('YYYY-MM-DD'),
  });
  const [drafts, setDrafts] = useState<CryovialDraft[]>(
    props.initialPositionIds.map((positionId) => ({
      positionId,
      barcode: '',
      overrides: {},
    })),
  );
  const [activeIds, setActiveIds] = useState(props.initialPositionIds);
  const [hasShared, setHasShared] = useState(
    props.initialPositionIds.length > 1,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [stage, setStage] = useState<'edit' | 'review'>('edit');
  const [reason, setReason] = useState('');
  const [issues, setIssues] = useState<CryovialIssue[]>([]);
  const [dirty, setDirty] = useState(false);
  const [picker, setPicker] = useState<string[] | null>(null);
  const [bulkField, setBulkField] = useState<CryovialField>('passage');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [undo, setUndo] = useState<CryovialDraft[] | null>(null);
  const [notice, setNotice] = useState('');
  const submitLock = useRef(false);
  const active = drafts.filter((row) => activeIds.includes(row.positionId));
  const multi = active.length > 1;
  const positionLabel = (id: string) =>
    props.positions.find((p) => p.id === id)?.label ?? id;
  const defaultMaterial = (line: string) =>
    props.materials.find((m) => m.cellLineId === line && m.type === 'PARENTAL')
      ?.id ?? '';
  const fieldLabel = (field: CryovialField | 'barcode') =>
    t(
      `samples.${({ cellLineId: 'cellLine', cellMaterialId: 'material' } as Record<string, string>)[field] ?? field}`,
    );
  const markDirty = () => {
    setDirty(true);
    setIssues([]);
    setUndo(null);
    setNotice('');
  };
  const updateRow = (id: string, field: CryovialField, value: string) => {
    markDirty();
    setDrafts((current) =>
      current.map((row) =>
        row.positionId === id
          ? updateCryovial(common, row, field, value, defaultMaterial)
          : row,
      ),
    );
  };
  const updateCommon = (field: CryovialField, value: string) => {
    if (!multi && active[0]) {
      updateRow(active[0].positionId, field, value);
      return;
    }
    markDirty();
    setCommon((current) => ({
      ...current,
      [field]: value,
      ...(field === 'cellLineId'
        ? { cellMaterialId: defaultMaterial(value) }
        : {}),
    }));
  };
  const close = () => {
    if (props.submitting) return;
    if (props.result || !dirty) {
      props.onClose();
      return;
    }
    modal.confirm({
      title: t('storage.entry.discardTitle'),
      content: t('storage.entry.discardHint'),
      okText: t('storage.entry.discard'),
      cancelText: t('storage.entry.keepEditing'),
      onOk: props.onClose,
    });
  };
  const input = (
    field: CryovialField,
    value: string,
    line: string,
    onChange: (value: string) => void,
    label: string,
  ) => {
    if (field === 'cellLineId' || field === 'cellMaterialId')
      return (
        <Select
          aria-label={label}
          showSearch
          allowClear
          optionFilterProp="label"
          value={value || undefined}
          onChange={(next: string | undefined) => onChange(next ?? '')}
          options={
            field === 'cellLineId'
              ? props.cellLines
              : props.materials
                  .filter((m) => m.cellLineId === line)
                  .map((m) => ({ value: m.id, label: m.label }))
          }
        />
      );
    if (field === 'freezeDate' || field === 'expiryDate')
      return (
        <DatePicker
          aria-label={label}
          value={value ? dayjs(value) : null}
          onChange={(date) => onChange(date?.format('YYYY-MM-DD') ?? '')}
          style={{ width: '100%' }}
        />
      );
    if (field === 'notes')
      return (
        <Input.TextArea
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
        />
      );
    return (
      <Input
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          field === 'passage'
            ? 'P12'
            : field === 'cellCount'
              ? '1e6'
              : undefined
        }
      />
    );
  };
  const origin = (row: CryovialDraft, field: CryovialField) => (
    <div className="cryovial-origin">
      {hasOverride(row, field) ? (
        <>
          <Typography.Text type="secondary">
            {t('storage.entry.independent')}
          </Typography.Text>
          <Button
            type="link"
            size="small"
            aria-label={`${positionLabel(row.positionId)} ${fieldLabel(field)} ${t('storage.entry.restore')}`}
            onClick={() => {
              markDirty();
              setDrafts((current) =>
                current.map((r) =>
                  r.positionId === row.positionId ? resetCryovial(r, field) : r,
                ),
              );
            }}
          >
            {t('storage.entry.restore')}
          </Button>
        </>
      ) : null}
    </div>
  );
  const renderField = (
    field: CryovialField,
    row?: CryovialDraft,
    showLabel = true,
  ) => {
    const values = row
      ? resolveCryovial(common, row)
      : multi
        ? common
        : resolveCryovial(common, active[0]!);
    const relatedIssues = issues.filter(
      (issue) =>
        issue.field === field &&
        (row
          ? issue.positionId === row.positionId
          : !multi ||
            !hasOverride(
              active.find((r) => r.positionId === issue.positionId)!,
              field,
            )),
    );
    const label = `${row ? positionLabel(row.positionId) : multi ? t('storage.entry.shared') : t('storage.entry.single')} ${fieldLabel(field)}`;
    return (
      <div
        key={field}
        className={field === 'notes' ? 'cryovial-wide' : undefined}
      >
        <Form.Item
          label={
            showLabel ? (
              <span className="cryovial-field-label">
                <span>{fieldLabel(field)}</span>
                {field === 'cellLineId' && (
                  <Button
                    type="link"
                    size="small"
                    onClick={props.onCreateCellLine}
                  >
                    {t('cellLines.create')}
                  </Button>
                )}
                {field === 'cellMaterialId' && (
                  <Button
                    type="link"
                    size="small"
                    disabled={!values.cellLineId}
                    onClick={() =>
                      props.onCreateMaterial(values.cellLineId ?? '')
                    }
                  >
                    {t('samples.createMaterial')}
                  </Button>
                )}
              </span>
            ) : undefined
          }
          required={
            field === 'cellLineId' ||
            field === 'freezeDate' ||
            field === 'ownerName'
          }
          validateStatus={relatedIssues.length ? 'error' : ''}
          help={
            relatedIssues.length
              ? t(`storage.entry.validation.${relatedIssues[0]!.code}`)
              : undefined
          }
          tooltip={
            showLabel && field === 'cellCount'
              ? t('samples.cellCountHint')
              : undefined
          }
        >
          {input(
            field,
            values[field] ?? '',
            values.cellLineId ?? '',
            (value) =>
              row
                ? updateRow(row.positionId, field, value)
                : updateCommon(field, value),
            label,
          )}
        </Form.Item>
        {row && multi && hasOverride(row, field) ? origin(row, field) : null}
      </div>
    );
  };
  const barcode = (row: CryovialDraft) => (
    <Form.Item
      label={!multi ? fieldLabel('barcode') : undefined}
      validateStatus={
        issues.some(
          (i) => i.positionId === row.positionId && i.field === 'barcode',
        )
          ? 'error'
          : ''
      }
      help={
        issues.some(
          (i) => i.positionId === row.positionId && i.field === 'barcode',
        )
          ? t('storage.entry.validation.duplicate')
          : undefined
      }
    >
      <Input
        aria-label={`${positionLabel(row.positionId)} ${fieldLabel('barcode')}`}
        value={row.barcode}
        placeholder={t('storage.entry.barcodeHint')}
        onChange={(e) => {
          markDirty();
          setDrafts((current) =>
            current.map((r) =>
              r.positionId === row.positionId
                ? { ...r, barcode: e.target.value }
                : r,
            ),
          );
        }}
      />
    </Form.Item>
  );
  const review = () => {
    const next = validateCryovials(common, active, props.materials);
    setIssues(next);
    if (next.length) return;
    setPicker(null);
    setStage('review');
    setNotice('');
  };
  const submit = async () => {
    if (submitLock.current || props.submitting) return;
    submitLock.current = true;
    try {
      await props.onSubmit(
        active.map((row) => resolveCryovial(common, row)),
        reason,
      );
    } finally {
      submitLock.current = false;
    }
  };
  const selected = active.filter((row) => selectedIds.includes(row.positionId));
  const styles = {
    '--storage-border': token.colorBorderSecondary,
    '--storage-surface': token.colorBgContainer,
    '--storage-muted-bg': token.colorFillAlter,
    '--storage-primary': token.colorPrimary,
    '--storage-soft': token.colorPrimaryBg,
  } as CSSProperties;
  const formatValue = (row: CryovialDraft, field: CryovialField) => {
    const value = resolveCryovial(common, row)[field];
    return (
      (field === 'cellLineId'
        ? props.cellLines.find((l) => l.value === value)?.code
        : field === 'cellMaterialId'
          ? props.materials.find((m) => m.id === value)?.label
          : value) || '—'
    );
  };
  return (
    <Modal
      open
      title={t('storage.createSample')}
      width={multi ? 1040 : 880}
      style={{ top: 24 }}
      onCancel={close}
      mask={{ closable: false }}
      keyboard={!props.submitting}
      closable={!props.submitting}
      className="cryovial-entry-modal"
      styles={{
        body: { maxHeight: 'calc(100dvh - 220px)', overflowY: 'auto' },
      }}
      footer={
        props.result ? (
          <Button onClick={props.onClose}>{t('common.close')}</Button>
        ) : (
          <div className="cryovial-footer">
            <Space wrap>
              {stage === 'review' && (
                <Button
                  disabled={props.submitting}
                  onClick={() => setStage('edit')}
                >
                  {t('storage.entry.back')}
                </Button>
              )}
              <Button disabled={props.submitting} onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                loading={props.submitting}
                onClick={() => {
                  if (stage === 'edit') review();
                  else void submit();
                }}
              >
                {stage === 'edit'
                  ? t('storage.entry.next')
                  : t(
                      props.canExecute
                        ? 'storage.batchPutawayConfirmExecution'
                        : 'storage.batchPutawaySubmitRequest',
                    )}
              </Button>
            </Space>
          </div>
        )
      }
    >
      {holder}
      <div className="cryovial-entry" style={styles}>
        <Steps
          size="small"
          current={props.result ? 2 : stage === 'edit' ? 0 : 1}
          status={props.result ? 'finish' : 'process'}
          items={[
            { title: t('storage.entry.fill') },
            { title: t('storage.entry.review') },
            { title: t('storage.entry.complete') },
          ]}
        />
        {props.result || (
          <>
            {stage === 'review' && (
              <Alert
                showIcon
                type="info"
                title={t(
                  props.canExecute
                    ? 'storage.entry.executeHint'
                    : 'storage.entry.requestHint',
                )}
              />
            )}
            <div className="cryovial-context">
              <Typography.Text type="secondary">{props.path}</Typography.Text>
              <Space wrap>
                <Tag>
                  {t('storage.entry.positions', { count: active.length })}
                </Tag>
                <span>
                  {active
                    .map((row) => positionLabel(row.positionId))
                    .join(' · ')}
                </span>
                {stage === 'edit' && (
                  <Button
                    type="link"
                    disabled={stage !== 'edit' || props.submitting}
                    onClick={() => setPicker(picker ? null : [...activeIds])}
                  >
                    {t('storage.entry.adjustPositions')}
                  </Button>
                )}
              </Space>
            </div>
            {picker && (
              <section className="cryovial-section">
                <Typography.Title level={5}>
                  {t('storage.entry.choosePositions')}
                </Typography.Title>
                <Checkbox.Group
                  value={picker}
                  onChange={(ids) => setPicker(ids.map(String))}
                  options={props.positions
                    .filter((p) => !p.occupied || activeIds.includes(p.id))
                    .map((p) => ({
                      label: p.label,
                      value: p.id,
                      disabled: p.occupied && !activeIds.includes(p.id),
                    }))}
                />
                <Typography.Paragraph type="secondary">
                  {t('storage.entry.positionHint')}
                </Typography.Paragraph>
                <Space>
                  <Button onClick={() => setPicker(null)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    disabled={!picker.length || picker.length > 100}
                    onClick={() => {
                      const ids = props.positions
                        .filter((p) => picker.includes(p.id))
                        .map((p) => p.id);
                      const firstSharedSource =
                        !hasShared && ids.length > 1 ? active[0] : undefined;
                      if (firstSharedSource) {
                        const values = resolveCryovial(
                          common,
                          firstSharedSource,
                        );
                        const shared: CryovialValues = {};
                        for (const field of cryovialFields)
                          shared[field] = values[field] ?? '';
                        setCommon(shared);
                        setHasShared(true);
                      }
                      setDrafts((current) => [
                        ...current.map((row) =>
                          row.positionId === firstSharedSource?.positionId
                            ? { ...row, overrides: {} }
                            : row,
                        ),
                        ...ids
                          .filter(
                            (id) =>
                              !current.some((row) => row.positionId === id),
                          )
                          .map((positionId) => ({
                            positionId,
                            barcode: '',
                            overrides: {},
                          })),
                      ]);
                      setActiveIds(ids);
                      setSelectedIds([]);
                      setPicker(null);
                      setBulkOpen(false);
                      markDirty();
                    }}
                  >
                    {t('storage.entry.usePositions')}
                  </Button>
                </Space>
                {picker.length > 100 && (
                  <Alert type="warning" title={t('storage.entry.maxCount')} />
                )}
              </section>
            )}
            {props.error && <Alert showIcon type="error" title={props.error} />}
            {stage === 'edit' ? (
              <Form
                layout="vertical"
                disabled={props.submitting}
                className={!multi ? 'cryovial-single-form' : undefined}
              >
                <section className="cryovial-section">
                  <div className="cryovial-section-heading">
                    <Typography.Title level={5}>
                      {t(
                        multi ? 'storage.entry.shared' : 'storage.entry.single',
                      )}
                    </Typography.Title>
                    {multi && (
                      <Tag color="blue">
                        {t('storage.entry.autoApply', { count: active.length })}
                      </Tag>
                    )}
                  </div>
                  {multi && (
                    <Typography.Paragraph type="secondary">
                      {t('storage.entry.sharedHint')}
                    </Typography.Paragraph>
                  )}
                  <div
                    className={`cryovial-fields ${multi ? '' : 'is-single'}`}
                  >
                    {(
                      [
                        'cellLineId',
                        'cellMaterialId',
                        'passage',
                        'freezeDate',
                        'cellCount',
                        'ownerName',
                        'label',
                      ] as CryovialField[]
                    ).map((field) => renderField(field))}
                    {!multi && active[0] && barcode(active[0])}
                  </div>
                  <Collapse
                    ghost
                    items={[
                      {
                        key: 'more',
                        label: t('storage.entry.extra'),
                        children: (
                          <div className="cryovial-fields">
                            {renderField('expiryDate')}
                            {renderField('notes')}
                          </div>
                        ),
                      },
                    ]}
                  />
                </section>
                {multi && (
                  <section className="cryovial-section cryovial-rows">
                    <div className="cryovial-section-heading">
                      <Typography.Title level={5}>
                        {t('storage.entry.rows')}
                      </Typography.Title>
                      <Typography.Text type="secondary">
                        {t('storage.entry.independentCount', {
                          count: active.filter(
                            (row) => Object.keys(row.overrides).length,
                          ).length,
                        })}
                      </Typography.Text>
                    </div>
                    <Typography.Paragraph type="secondary">
                      {t('storage.entry.rowsHint')}
                    </Typography.Paragraph>
                    <Button
                      type="link"
                      onClick={() => {
                        setBulkMode(!bulkMode);
                        setBulkOpen(false);
                        setSelectedIds([]);
                      }}
                    >
                      {t(
                        bulkMode
                          ? 'storage.entry.endBulkEdit'
                          : 'storage.entry.startBulkEdit',
                      )}
                    </Button>
                    {bulkMode && (
                      <div className="cryovial-bulkbar">
                        <span aria-live="polite">
                          {t('storage.entry.selectedCount', {
                            count: selected.length,
                          })}
                        </span>
                        <Space wrap>
                          <Button
                            disabled={!selected.length}
                            onClick={() => {
                              setBulkValue(common[bulkField] ?? '');
                              setBulkOpen(!bulkOpen);
                            }}
                          >
                            {t('storage.entry.editSelected')}
                          </Button>
                          {undo && (
                            <Button
                              type="link"
                              onClick={() => {
                                setDrafts((current) =>
                                  current.map((row) => {
                                    const previous = undo.find(
                                      (old) =>
                                        old.positionId === row.positionId,
                                    );
                                    return previous
                                      ? {
                                          ...row,
                                          overrides: previous.overrides,
                                        }
                                      : row;
                                  }),
                                );
                                setUndo(null);
                                setIssues([]);
                                setNotice(t('storage.entry.undone'));
                              }}
                            >
                              {t('storage.entry.undo')}
                            </Button>
                          )}
                        </Space>
                      </div>
                    )}
                    {bulkOpen && (
                      <div className="cryovial-bulkeditor">
                        <Space wrap>
                          <Select
                            aria-label={t('storage.entry.field')}
                            value={bulkField}
                            onChange={(field) => {
                              setBulkField(field);
                              setBulkValue(common[field] ?? '');
                            }}
                            options={(
                              [
                                'passage',
                                'label',
                                'ownerName',
                                'cellCount',
                                'freezeDate',
                                'expiryDate',
                                'notes',
                              ] as CryovialField[]
                            ).map((field) => ({
                              value: field,
                              label: fieldLabel(field),
                            }))}
                          />
                          {input(
                            bulkField,
                            bulkValue,
                            '',
                            setBulkValue,
                            t('storage.entry.bulkValue'),
                          )}
                          <Button
                            disabled={
                              !selected.length ||
                              (bulkField === 'freezeDate' && !bulkValue)
                            }
                            onClick={() => {
                              setUndo(
                                selected.map((row) => ({
                                  ...row,
                                  overrides: { ...row.overrides },
                                })),
                              );
                              setDrafts((current) =>
                                current.map((row) =>
                                  selectedIds.includes(row.positionId) &&
                                  activeIds.includes(row.positionId)
                                    ? updateCryovial(
                                        common,
                                        row,
                                        bulkField,
                                        bulkValue,
                                        defaultMaterial,
                                      )
                                    : row,
                                ),
                              );
                              setIssues([]);
                              setDirty(true);
                              setBulkOpen(false);
                              setNotice(
                                t('storage.entry.applied', {
                                  count: selected.length,
                                  field: fieldLabel(bulkField),
                                }),
                              );
                            }}
                          >
                            {t('storage.entry.applyCount', {
                              count: selected.length,
                            })}
                          </Button>
                        </Space>
                        <Typography.Paragraph type="secondary">
                          {t('storage.entry.overwriteHint', {
                            positions: selected
                              .map((row) => positionLabel(row.positionId))
                              .join(' · '),
                            field: fieldLabel(bulkField),
                          })}
                        </Typography.Paragraph>
                      </div>
                    )}
                    <Table<CryovialDraft>
                      rowKey="positionId"
                      size="small"
                      pagination={false}
                      dataSource={active}
                      scroll={{ x: 700 }}
                      {...(bulkMode
                        ? {
                            rowSelection: {
                              selectedRowKeys: selectedIds,
                              onChange: (keys: React.Key[]) => {
                                setSelectedIds(keys.map(String));
                                setBulkOpen(false);
                              },
                            },
                          }
                        : {})}
                      expandable={{
                        expandedRowRender: (row) => (
                          <div className="cryovial-fields">
                            {(
                              [
                                'cellLineId',
                                'cellMaterialId',
                                'freezeDate',
                                'cellCount',
                                'ownerName',
                                'expiryDate',
                                'notes',
                              ] as CryovialField[]
                            ).map((field) => renderField(field, row))}
                          </div>
                        ),
                        columnTitle: t('storage.entry.more'),
                        columnWidth: 72,
                        expandIcon: ({ expanded, onExpand, record }) => (
                          <Button
                            type="text"
                            size="small"
                            className="cryovial-expand-button"
                            aria-label={`${positionLabel(record.positionId)} ${t('storage.entry.more')}`}
                            aria-expanded={expanded}
                            onClick={(event) => onExpand(record, event)}
                          >
                            {expanded ? <MinusOutlined /> : <PlusOutlined />}
                          </Button>
                        ),
                      }}
                      columns={[
                        {
                          title: t('storage.position'),
                          width: 65,
                          render: (_, row) => (
                            <strong>{positionLabel(row.positionId)}</strong>
                          ),
                        },
                        {
                          title: fieldLabel('label'),
                          width: 230,
                          render: (_, row) => renderField('label', row, false),
                        },
                        {
                          title: fieldLabel('passage'),
                          width: 150,
                          render: (_, row) =>
                            renderField('passage', row, false),
                        },
                        {
                          title: fieldLabel('barcode'),
                          width: 200,
                          render: (_, row) => barcode(row),
                        },
                      ]}
                    />
                    <Typography.Text role="status">{notice}</Typography.Text>
                  </section>
                )}
                <section className="cryovial-section">
                  <Form.Item label={t('storage.batchPutawayReason')}>
                    <Input.TextArea
                      value={reason}
                      onChange={(e) => {
                        markDirty();
                        setReason(e.target.value);
                      }}
                      rows={2}
                      maxLength={500}
                      showCount
                      placeholder={t('storage.batchPutawayReasonPlaceholder')}
                    />
                  </Form.Item>
                </section>
                {issues.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    title={t('storage.entry.checkErrors')}
                    description={
                      <ul>
                        {issues.map((issue) => (
                          <li
                            key={`${issue.positionId}-${issue.field}-${issue.code}`}
                          >
                            {positionLabel(issue.positionId)} ·{' '}
                            {fieldLabel(issue.field)}:{' '}
                            {t(`storage.entry.validation.${issue.code}`)}
                          </li>
                        ))}
                      </ul>
                    }
                  />
                )}
              </Form>
            ) : (
              <section className="cryovial-section">
                <Table<CryovialDraft>
                  rowKey="positionId"
                  dataSource={active}
                  size="small"
                  pagination={false}
                  scroll={{ x: 520 }}
                  tableLayout="fixed"
                  expandable={{
                    expandedRowRender: (row) => (
                      <Descriptions
                        size="small"
                        column={{ xs: 1, sm: 2 }}
                        items={[
                          {
                            key: 'barcode',
                            label: fieldLabel('barcode'),
                            children: row.barcode.trim() || '—',
                          },
                          ...cryovialFields.map((field) => ({
                            key: field,
                            label: fieldLabel(field),
                            children: (
                              <>
                                {formatValue(row, field)}{' '}
                                {multi && hasOverride(row, field) && (
                                  <Tag>{t('storage.entry.independent')}</Tag>
                                )}
                              </>
                            ),
                          })),
                        ]}
                      />
                    ),
                  }}
                  columns={[
                    {
                      title: t('storage.position'),
                      width: 65,
                      render: (_, row) => positionLabel(row.positionId),
                    },
                    ...(
                      [
                        'cellLineId',
                        'cellMaterialId',
                        'passage',
                        'freezeDate',
                      ] as CryovialField[]
                    ).map((field) => ({
                      title: fieldLabel(field),
                      render: (_: unknown, row: CryovialDraft) =>
                        formatValue(row, field),
                    })),
                    {
                      title: fieldLabel('ownerName'),
                      render: (_, row) => formatValue(row, 'ownerName'),
                    },
                  ]}
                />
                <Typography.Paragraph className="cryovial-review-reason">
                  {t('storage.batchPutawayReason')}: {reason || '—'}
                </Typography.Paragraph>
              </section>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
